import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useUserProfile } from "../../shared/providers/UserProfileProvider";
import { useRemainingCredits } from "../../shared/providers/RemainingCreditsProvider";
import { CancellationModal } from "./CancellationModal";
import { envConfig } from "../../shared/constants";
import fetchWithRetry from "../../shared/utils/fetch-with-retry";
import { getAffiliate } from "../../shared/utils/affiliateStorage";

const APPSUMO_TIER_LABELS: Record<number, string> = {
  200_000: "Lifetime Tier 1",
  500_000: "Lifetime Tier 2",
  1_250_000: "Lifetime Tier 3",
};

interface PricingPlanProps {
  name: string;
  price: string;
  period: string;
  buttonText: string;
  buttonStyle: "default" | "primary" | "secondary" | "cancel";
  isPopular?: boolean;
  features: string[];
  delay: number;
  onButtonClick?: () => void;
  disabled?: boolean;
  seats?: number;
  onSeatsChange?: (seats: number) => void;
  showSeatsSelector?: boolean;
}

const PricingPlan: React.FC<PricingPlanProps> = ({
  name,
  price,
  period,
  buttonText,
  buttonStyle,
  isPopular,
  features,
  delay,
  onButtonClick,
  disabled = false,
  seats,
  onSeatsChange,
  showSeatsSelector = false,
}) => {
  const [localSeats, setLocalSeats] = useState(seats || 3);

  const getButtonClass = () => {
    if (disabled) {
      return "bg-gray-100 text-gray-400 cursor-not-allowed";
    }
    switch (buttonStyle) {
      case "primary":
        return "bg-sky-500 text-white hover:bg-sky-600";
      case "secondary":
        return "bg-gray-900 text-white hover:bg-gray-800";
      case "cancel":
        return "text-red-600 hover:text-red-700";
      default:
        return "bg-gray-200 text-gray-700 hover:bg-gray-300";
    }
  };

  const handleSeatsChange = (newSeats: number) => {
    setLocalSeats(newSeats);
    onSeatsChange?.(newSeats);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`bg-white rounded-xl p-6 ${
        isPopular ? "ring-2 ring-sky-500" : ""
      } relative`}
    >
      {isPopular && (
        <div className="absolute -top-3 right-6">
          <span className="px-3 py-1 bg-sky-500 text-white rounded-full text-xs font-medium">
            Popular
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2">
            {name}
          </h3>
          <div className="flex items-baseline">
            <span className="text-2xl font-extralight text-gray-900">
              {price}
            </span>
            <span className="text-sm text-gray-500">{period}</span>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: disabled ? 1 : 1.02 }}
          whileTap={{ scale: disabled ? 1 : 0.98 }}
          onClick={onButtonClick}
          disabled={disabled}
          className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${getButtonClass()}`}
        >
          {buttonText}
        </motion.button>
      </div>

      {/* Seats Selector for Business Plan */}
      {showSeatsSelector && (
        <div className="py-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600">Team seats</label>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleSeatsChange(Math.max(3, localSeats - 1))}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <span className="text-gray-600">−</span>
              </button>
              <span className="w-12 text-center text-sm font-medium">
                {localSeats}
              </span>
              <button
                onClick={() => handleSeatsChange(localSeats + 1)}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <span className="text-gray-600">+</span>
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Minimum 3 seats required</p>
        </div>
      )}

      <div className="pt-4 border-t border-gray-100">
        <ul className="grid grid-cols-2 gap-3">
          {features.map((feature, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: delay + index * 0.05 }}
              className="flex items-center text-xs text-gray-600"
            >
              <span className="text-sky-500 mr-2">✓</span>
              {feature}
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

interface PricingSectionProps {
  onCloseModal?: () => void;
  onUpgradeSuccess?: (planName: string) => void;
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  onCloseModal,
  onUpgradeSuccess,
}) => {
  const { userProfile, accessToken } = useUserProfile();
  const {
    usage,
    limits,
    isLoading: creditsLoading,
    refreshCredits,
  } = useRemainingCredits();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [businessSeats, setBusinessSeats] = useState(3);
  const [isProcessing, setIsProcessing] = useState(false);

  // Use real-time data from useRemainingCredits for current plan state
  const realtimePlan = limits.planType || "FREE";
  const isUnlimited = usage.isUnlimited || limits.isUnlimited;

  // Fallback to userProfile for subscription status only
  const subscriptionStatus = userProfile?.Billing?.subscription_status;

  // Track if checkout was opened and previous plan
  const [checkoutOpened, setCheckoutOpened] = useState(false);
  const [previousPlan, setPreviousPlan] = useState<string | null>(null);

  // Auto-refresh when window regains focus after checkout
  useEffect(() => {
    if (!checkoutOpened) return;

    const handleFocus = async () => {

      // Refresh credits to check for plan changes
      await refreshCredits();

      // Wait a bit for the state to update
      setTimeout(() => {
        const newPlan = limits.planType || userProfile?.Billing?.plan_name;

        if (newPlan !== "FREE" && newPlan !== previousPlan) {

          // Call the success handler which will close modal and show celebration
          if (onUpgradeSuccess) {
            const planDisplayName =
              newPlan === "UNLIMITED" ? "Unlimited" : "Business";
            onUpgradeSuccess(planDisplayName);
          }

          setCheckoutOpened(false);
          setPreviousPlan(null);
        }
      }, 500); // Small delay to ensure state updates
    };

    // Listen for window focus events
    window.addEventListener("focus", handleFocus);

    // Also poll every 10 seconds for updates (less aggressive)
    const pollInterval = setInterval(async () => {
      await refreshCredits();

      // Wait a bit for state to update
      setTimeout(() => {
        const newPlan = limits.planType || userProfile?.Billing?.plan_name;
        if (newPlan !== "FREE" && newPlan !== previousPlan) {

          // Call the success handler which will close modal and show celebration
          if (onUpgradeSuccess) {
            const planDisplayName =
              newPlan === "UNLIMITED" ? "Unlimited" : "Business";
            onUpgradeSuccess(planDisplayName);
          }

          setCheckoutOpened(false);
          setPreviousPlan(null);
        }
      }, 500);
    }, 10000); // Poll every 10 seconds

    return () => {
      window.removeEventListener("focus", handleFocus);
      clearInterval(pollInterval);
    };
  }, [checkoutOpened, previousPlan, refreshCredits, limits, userProfile]);

  // LemonSqueezy variant IDs
  const variantIds = {
    unlimited: {
      monthly: "931718",
      yearly: "931717",
    },
    business: {
      monthly: "931716",
      yearly: "931715",
    },
  };

  const isOnPlan = (plan: "FREE" | "UNLIMITED" | "BUSINESS"): boolean => {
    // Use real-time plan from credits hook
    if (plan === "FREE") {
      return realtimePlan === "FREE";
    }
    // For paid plans, check both plan type and that it's not free
    return realtimePlan === plan || (plan === "UNLIMITED" && isUnlimited);
  };

  const handleCheckout = async (plan: "unlimited" | "business") => {

    const variantId = variantIds[plan][billingPeriod];

    setIsProcessing(true);

    try {
      // Create checkout URL via backend API
      const response = await fetchWithRetry(
        `${envConfig.EXPRESS_BACKEND_URL}/api/lemonsqueezy/create-checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            variantId,
            seats: plan === "business" ? businessSeats : 1,
            affiliateId: getAffiliate(), // Include affiliate ID if present
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create checkout");
      }

      const { checkoutUrl } = await response.json();

      // Open checkout in new tab for web
      window.open(checkoutUrl, "_blank");

      // Save current plan and mark that checkout was opened
      setPreviousPlan(realtimePlan);
      setCheckoutOpened(true);
    } catch (error) {
      console.error("Error creating checkout:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async (reason: string) => {
    setIsProcessing(true);
    try {
      const response = await fetchWithRetry(
        `${envConfig.EXPRESS_BACKEND_URL}/api/lemonsqueezy/cancel-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            subscription_id: userProfile?.Billing?.subscription_id,
            reason,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to cancel subscription");
      }

      // Send cancellation feedback to Slack
      await fetchWithRetry(
        `${envConfig.EXPRESS_BACKEND_URL}/api/notify-cancellation-feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            feedback: reason,
          }),
        }
      ).catch(() => {});

      window.location.reload();
    } catch (error) {
      console.error("Error cancelling subscription:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateSubscription = async () => {
    setIsProcessing(true);
    try {
      // Get signed customer portal URL from API
      const response = await fetchWithRetry(
        `${envConfig.EXPRESS_BACKEND_URL}/api/lemonsqueezy/customer-portal`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get customer portal URL");
      }

      const { portalUrl } = await response.json();

      // Open portal in new tab for web
      window.open(portalUrl, "_blank");
    } catch (error) {
      console.error("Error opening customer portal:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsProcessing(true);
    try {
      // Get signed customer portal URL from API
      const response = await fetchWithRetry(
        `${envConfig.EXPRESS_BACKEND_URL}/api/lemonsqueezy/customer-portal`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get customer portal URL");
      }

      const { portalUrl } = await response.json();

      // Open portal in new tab for web
      window.open(portalUrl, "_blank");
    } catch (error) {
      console.error("Error opening customer portal:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // AppSumo LTD state
  if (limits?.isAppSumo) {
    const consumed = usage?.consumedWords ?? 0;
    const total = limits?.wordLimit ?? 75000;
    const percentage = Math.min(100, Math.round((consumed / total) * 100));

    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto mb-12"
        >
          {/* AppSumo badge */}
          <div className="flex items-center gap-2 mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full">
              <span className="text-sm">🌮</span>
              <span className="text-xs font-medium text-orange-700">
                Sumoling
              </span>
            </span>
          </div>

          <h2 className="text-2xl font-light text-gray-900 mb-1">
            Lifetime Deal
          </h2>
          <p className="text-sm text-gray-500 mb-8">
            Hey Sumoling! You're on the Lifetime plan from AppSumo
          </p>

          {/* Plan card */}
          <div className="bg-orange-50 rounded-xl p-6 mb-4">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {APPSUMO_TIER_LABELS[total] ?? "Lifetime Unlimited"}
                  </h3>
                  <p className="text-xs text-green-600">Active — No expiration</p>
                </div>
              </div>
            </div>

            <div className="border-t border-orange-200 pt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Words used this month</span>
                <span className="text-sm font-semibold text-gray-900">
                  {consumed.toLocaleString()} / {total.toLocaleString()}
                </span>
              </div>
              <div className="w-full h-2 bg-orange-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className={`h-full rounded-full ${percentage >= 90 ? "bg-red-500" : "bg-orange-500"}`}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                {(total - consumed).toLocaleString()} words remaining
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="bg-white rounded-xl p-6 mb-8">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-4 font-medium">
              Included in your plan
            </p>
            <ul className="grid grid-cols-2 gap-3">
              {[
                "Voice dictation",
                "AI rewrite styles",
                "120+ languages",
                "iOS + Mac + Web",
                "Free updates forever",
                "Custom vocabulary",
              ].map((feature, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center text-xs text-gray-600"
                >
                  <span className="text-orange-500 mr-2">✓</span>
                  {feature}
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Switch to monthly */}
          <div className="border-t border-gray-100 pt-6">
            <p className="text-xs text-gray-400 text-center mb-3">
              Want unlimited words? Switch to a monthly plan
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleCheckout("unlimited")}
              disabled={isProcessing}
              className="w-full px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Switch to Unlimited Monthly — $15/mo
            </motion.button>
          </div>
        </motion.div>
      </>
    );
  }

  return (
    <>
      {/* Manage Subscription Button - Show for paid users */}
      {(isOnPlan("UNLIMITED") || isOnPlan("BUSINESS")) && (
        <div className="flex justify-center mb-8">
          <motion.button
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleManageSubscription}
            disabled={isProcessing}
            className="px-6 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span>Manage Subscription</span>
          </motion.button>
        </div>
      )}

      {/* Billing Period Toggle */}
      <div className="flex justify-center mb-12">
        <div className="bg-gray-50 p-1 rounded-lg">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setBillingPeriod("monthly")}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
              billingPeriod === "monthly"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Monthly
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setBillingPeriod("yearly")}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
              billingPeriod === "yearly"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Yearly
            <span className="ml-1 text-xs text-sky-500">Save 20%</span>
          </motion.button>
        </div>
      </div>

      <div className="space-y-4 max-w-4xl mx-auto">
        {/* Starter Plan */}
        <div>
          <PricingPlan
            name="Starter"
            price="$0"
            period="/mo"
            buttonText={isOnPlan("FREE") ? "Current Plan" : "Downgrade"}
            buttonStyle="default"
            disabled={isOnPlan("FREE") || isProcessing}
            onButtonClick={() => {
              // If user has a paid plan and clicks downgrade, show cancellation modal
              if (realtimePlan !== "FREE") {
                setShowCancellationModal(true);
              }
            }}
            features={[
              "2,000 words/month",
              "1 hour max/recording",
              "Basic AI enhancement",
              "Works everywhere",
              "Community support",
            ]}
            delay={0.1}
          />
        </div>

        {/* Unlimited Plan */}
        <div>
          <PricingPlan
            name="Unlimited"
            price={`$${billingPeriod === "monthly" ? "15" : "12"}`}
            period="/mo"
            buttonText={isOnPlan("UNLIMITED") ? "Current Plan" : "Get Started"}
            buttonStyle={isOnPlan("UNLIMITED") ? "default" : "primary"}
            disabled={isOnPlan("UNLIMITED") || isProcessing}
            onButtonClick={() =>
              !isOnPlan("UNLIMITED") && handleCheckout("unlimited")
            }
            isPopular
            features={[
              "Unlimited dictation",
              "2 hours max/recording",
              "MAX mode",
              "Advanced AI models",
              "Custom vocabulary",
              "Export history",
              "Priority support",
            ]}
            delay={0.2}
          />
          {isOnPlan("UNLIMITED") && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowCancellationModal(true)}
              disabled={isProcessing}
              className="mt-3 w-full px-6 py-2 text-sm text-red-600 hover:text-red-700 transition-colors"
            >
              Cancel Subscription
            </motion.button>
          )}
        </div>

        {/* Business Plan */}
        <div>
          <PricingPlan
            name="Business"
            price={`$${billingPeriod === "monthly" ? "9" : "7"}`}
            period="/seat"
            buttonText={
              isOnPlan("BUSINESS") ? "Update Subscription" : "Get Started"
            }
            buttonStyle={isOnPlan("BUSINESS") ? "primary" : "secondary"}
            disabled={isProcessing}
            onButtonClick={() => {
              if (isOnPlan("BUSINESS")) {
                handleUpdateSubscription();
              } else {
                handleCheckout("business");
              }
            }}
            seats={businessSeats}
            onSeatsChange={setBusinessSeats}
            showSeatsSelector={!isOnPlan("BUSINESS")}
            features={[
              "Unlimited dictation",
              "2 hours max/recording",
              "MAX mode",
              "Everything in Unlimited",
              "Team management",
              "Usage analytics",
              "Dedicated support",
              "Custom contracts",
            ]}
            delay={0.3}
          />
          {isOnPlan("BUSINESS") && (
            <>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Current seats:{" "}
                    {userProfile?.Billing?.subscription_count || 1}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setBusinessSeats(Math.max(3, businessSeats - 1))
                      }
                      className="w-8 h-8 rounded-lg bg-white hover:bg-gray-100 flex items-center justify-center transition-colors"
                    >
                      <span className="text-gray-600">−</span>
                    </button>
                    <span className="w-12 text-center text-sm font-medium">
                      {businessSeats}
                    </span>
                    <button
                      onClick={() => setBusinessSeats(businessSeats + 1)}
                      className="w-8 h-8 rounded-lg bg-white hover:bg-gray-100 flex items-center justify-center transition-colors"
                    >
                      <span className="text-gray-600">+</span>
                    </button>
                  </div>
                </div>
              </div>
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCancellationModal(true)}
                disabled={isProcessing}
                className="mt-3 w-full px-6 py-2 text-sm text-red-600 hover:text-red-700 transition-colors"
              >
                Cancel Subscription
              </motion.button>
            </>
          )}
        </div>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-xs text-gray-400 text-center mt-12"
      >
        All plans include end-to-end encryption and work with every app
      </motion.p>

      <CancellationModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        onSubmit={handleCancelSubscription}
      />
    </>
  );
};
